// src/components/presaleLandingP.tsx
// ============================================================================
// FILE ROLE: Landing + authentication stage controller.
// - Stages: signup | signin | forgot-password | welcome
// - Renders marketing + PlanSelector for signup flow
// - Handles manual sign-in and routes to "welcome" on success
// - Listens for auth signedOut events and returns user to signin (when appropriate)
// TRUST BOUNDARY: public/unauth → authed user view when stage === "welcome" (UserData)
// ============================================================================

import React, { useState, useEffect, useRef } from "react";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";

import { signInUser } from "../auth/cognitoAuth";
import Header from "./header";
import MarketingCards from "./marketingCards";
import StickyHeader from "./StickyHeader";
import StickyFooter from "./StickyFooter";
import UserData from "./UserData";
import PlanSelector from "./PlanSelector";
import ForgotPassword from "./ForgotPassword";
import LocalCrushList, { readPresignupStore } from "./LocalCrushList";
import LocalHandleList, { readPresignupHandleStore } from "./LocalHandleList";
import ZodiacPhotoFrame from "./ZodiacPhotoFrame";
import { type StandaloneSignUpFormHandle } from "./StandaloneSignUpForm";
import LockedCardOverlay from "./LockedCardOverlay";

type Stage = "signup" | "signin" | "forgot-password" | "welcome";

// ---- Founder's Note copy ---------------------------------------------------
// Edit FOUNDERS_NOTE_BODY to replace the placeholder with real copy.
const FOUNDERS_NOTE_TITLE       = "Why I Built Crushhh";
const FOUNDERS_NOTE_BODY        = `"I believe everyone misses out at least once in their life. I don't want people to look back and realize they missed out on something they didn't realize was even possible. So I spent 4 years building Crushhh to make it easy to see what's already there. We all sit on the fence while we wait for the right moment. Now anyone can test the waters without putting themselves out there. If you match you'll both know and no one will know anything if you don't. Crushes make better matches, or at least I think so."`;
const FOUNDERS_NOTE_ATTRIBUTION = " J.C., Founder and Hopeless Romantic";
// ---------------------------------------------------------------------------

// Step 2 (booth) completion — persisted so returning users resume at Step 3.
const BOOTH_DONE_KEY = "crushhh_step2_done";

function readBoothDone(): boolean {
  try { return localStorage.getItem(BOOTH_DONE_KEY) === "1"; } catch { return false; }
}

function writeBoothDone(): void {
  try { localStorage.setItem(BOOTH_DONE_KEY, "1"); } catch {}
}

// ---- Accordion card system — mirrors UserData.tsx sectionCardStyle / sectionHeaderStyle
// so landing and profile share the same card frame. Header CONTENT keeps the landing's
// existing treatment (number badge + large ADD/POST/MATCH) rather than the 20px profile size.

const sectionCardStyle: React.CSSProperties = {
  background: "#FFFFFF",
  border: "3px solid #1A1A1A",
  borderRadius: 20,
  boxShadow: "6px 6px 0px #1A1A1A",
  marginBottom: 16,
  overflow: "hidden",
  position: "relative",
};

const STEP_BADGE_WIDTH = "clamp(68px, 24vw, 100px)";
const STEP_LABEL_FONT  = "clamp(26px, 9vw, 36px)";

function stepHeaderStyle(_expanded: boolean, unlocked: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 18,
    padding: "24px 26px",
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: "none",
    cursor: unlocked ? "pointer" : "default",
    textAlign: "left" as const,
    outline: "none",
    boxShadow: "none",
    boxSizing: "border-box" as const,
  };
}

const accordionBodyStyle: React.CSSProperties = {
  padding: "2px 16px 20px",
};

function ChevronSvg({ open }: { open: boolean }) {
  return (
    <svg
      width="22"
      height="14"
      viewBox="0 0 12 8"
      fill="none"
      aria-hidden="true"
      style={{
        flexShrink: 0,
        marginLeft: "auto",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.22s ease",
      }}
    >
      <path d="M1 1L6 7L11 1" stroke="#888" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const PresaleLandingP: React.FC = () => {
  // ============================================================================
  // STAGE + FORM STATE
  // ============================================================================
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    (navigator as any).standalone;

  const [stage, setStage] = useState<Stage>(isStandalone ? "signin" : "signup");

  // ============================================================================
  // ACCORDION STATE
  // ============================================================================

  // Which step is currently open. Derived initial value: resume returning users
  // at the step they left off at.
  const [openStep, setOpenStep] = useState<1 | 2 | 3 | null>(null);

  // Step 1 — crush list (entry count not directly consumed; confirmed count drives the gate)
  const [hasConfirmedCrush, setHasConfirmedCrush] = useState(() => readPresignupStore().some((e) => e.verified));
  // Handle sub-section — hidden until first crush confirmed; one-way latch (stays shown).
  const [showHandleSection, setShowHandleSection] = useState(() => readPresignupStore().some((e) => e.verified));
  // Track whether showHandleSection was already true on mount so the reveal scroll
  // doesn't fire for returning users (it only fires on the first->true transition).
  const showHandleSectionInitialRef = useRef<boolean>(readPresignupStore().some((e) => e.verified));

  // Step 1 — handle list
  const [_hasHandleEntries, setHasHandleEntries] = useState(() => readPresignupHandleStore().length > 0);
  const [hasConfirmedHandle, setHasConfirmedHandle] = useState(() => readPresignupHandleStore().filter((e) => e.confirmed).length > 0);

  // Step 2 — booth done (posted or skipped)
  const [boothDone, setBoothDone] = useState(readBoothDone);

  // Derived gates
  const step2Unlocked = hasConfirmedCrush && hasConfirmedHandle;
  const step3Unlocked = step2Unlocked && boothDone;

  // Ref for the handle sub-section inside Step 1 — used by the reveal scroll effect.
  const handleSubRef = useRef<HTMLDivElement | null>(null);
  const step1CardRef = useRef<HTMLDivElement | null>(null);
  const step2CardRef = useRef<HTMLDivElement | null>(null);
  const step3CardRef = useRef<HTMLDivElement | null>(null);

  // ============================================================================
  // FORM / AUTH STATE
  // ============================================================================
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [errorMessage,  setErrorMessage]  = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [isSigningIn,   setIsSigningIn]   = useState(false);

  const signUpFormRef = useRef<StandaloneSignUpFormHandle>(null);
  const emailRef      = useRef<HTMLInputElement | null>(null);

  // ============================================================================
  // SESSION CHECK ON MOUNT
  // ============================================================================
  useEffect(() => {
    (async () => {
      try {
        const s = await fetchAuthSession({ forceRefresh: true });
        if (s.tokens?.accessToken) {
          const groups = s.tokens.accessToken.payload['cognito:groups'] as string[] | undefined;
          if (groups?.includes('Admins')) {
            window.location.hash = '#/admin/';
          } else {
            setStage("welcome");
          }
        }
      } catch {}
    })();
  }, []);

  // ============================================================================
  // AUTO-SCROLL ON STAGE CHANGE
  // ============================================================================
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setErrorMessage("");
  }, [stage]);

  useEffect(() => {
    if (stage === "signin") {
      emailRef.current?.focus();
      emailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [stage]);

  // ============================================================================
  // AUTH EVENT LISTENER: signedOut
  // ============================================================================
  useEffect(() => {
    const hubListenerCancel = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedOut") {
        if (stage === "welcome") {
          setStage("signin");
          setEmail("");
          setPassword("");
          setErrorMessage("");
        }
      }
    });
    return () => hubListenerCancel();
  }, [stage]);

  // ============================================================================
  // ACCORDION AUTO-ADVANCE: fire only on the initial gate-unlock transition.
  // Initialized to true when the gate is already satisfied on mount so returning
  // users never get forcibly bounced forward. Resets to false if the gate drops
  // (e.g. user removes all handles), allowing re-advance on the next unlock.
  // ============================================================================
  const step2AutoAdvancedRef = useRef<boolean>(step2Unlocked);
  const step3AutoAdvancedRef = useRef<boolean>(step3Unlocked);

  useEffect(() => {
    if (!step2Unlocked) { step2AutoAdvancedRef.current = false; return; }
    if (step2AutoAdvancedRef.current) return;
    step2AutoAdvancedRef.current = true;
    setOpenStep(2);
  }, [step2Unlocked]);

  useEffect(() => {
    if (!step3Unlocked) { step3AutoAdvancedRef.current = false; return; }
    if (step3AutoAdvancedRef.current) return;
    step3AutoAdvancedRef.current = true;
    setOpenStep(3);
  }, [step3Unlocked]);

  // ============================================================================
  // HANDLE REVEAL SCROLL: fires after the handle sub-section mounts for the
  // first time (new interaction, not returning-user initial state).
  // ============================================================================
  useEffect(() => {
    if (!showHandleSection) return;
    if (showHandleSectionInitialRef.current) return; // already true on mount — skip
    setTimeout(() => {
      const target = handleSubRef.current;
      if (!target) return;
      const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
      const bandHeight = bandEl ? bandEl.offsetHeight : 0;
      target.style.scrollMarginTop = `${bandHeight + 8}px`;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  }, [showHandleSection]);

  // Card-expand scroll: whenever a step opens, scroll that card's top edge just
  // below the sticky banner so the header is immediately visible.
  useEffect(() => {
    if (openStep === null) return;
    const cardRef = openStep === 1 ? step1CardRef : openStep === 2 ? step2CardRef : step3CardRef;
    setTimeout(() => {
      const card = cardRef.current;
      if (!card) return;
      const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
      const bannerHeight = bandEl ? bandEl.offsetHeight : 0;
      card.style.scrollMarginTop = `${bannerHeight + 8}px`;
      card.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [openStep]);

  // ============================================================================
  // SIGN-IN HANDLER
  // ============================================================================
  const handleSignIn = async () => {
    setErrorMessage("");
    setIsSigningIn(true);

    if (!email.trim()) {
      setErrorMessage("Please enter your email.");
      setIsSigningIn(false);
      return;
    }
    if (!password.trim()) {
      setErrorMessage("Please enter your password.");
      setIsSigningIn(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSigningIn(false);
      setErrorMessage("Sign-in is taking too long. Please try again or check your connection.");
    }, 15000);

    try {
      try {
        await getCurrentUser();
        await signOut({ global: true });
      } catch {
        // no-op
      }

      await signInUser(email, password);
      const session = await fetchAuthSession({ forceRefresh: true });

      clearTimeout(timeoutId);

      if (session.tokens?.accessToken) {
        setStage("welcome");
        setEmail("");
        setPassword("");
        setErrorMessage("");
        setIsSigningIn(false);
      } else {
        setErrorMessage("Invalid email or password. Please try again.");
        setIsSigningIn(false);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      setErrorMessage("Incorrect email or password.");
      void ["Sign-in error:", error];
      setIsSigningIn(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="min-h-screen w-full relative">
      <StickyHeader />

      <div className="max-w-screen-lg mx-auto py-1 bg-transparent mt-10 md:mt-12 mb-8">
        <Header onLogoClick={() => setStage("signup")} />

        {errorMessage && (
          <p className="text-branddark text-sm text-center mt-4">{errorMessage}</p>
        )}

        {/* WELCOME: Logged-in user experience */}
        {stage === "welcome" && (
          <div>
            <UserData setStage={setStage} />
          </div>
        )}

        {/* SIGNUP: 3-step functional accordion */}
        {stage === "signup" && (
          <>
            {/* ── 3-step accordion ── */}
            <div style={{ padding: "20px 16px" }}>

              {/* ── STEP 1: ADD ── always unlocked */}
              <div ref={step1CardRef} style={sectionCardStyle}>
                <button
                  type="button"
                  onClick={() => setOpenStep(openStep === 1 ? null : 1)}
                  aria-expanded={openStep === 1}
                  style={stepHeaderStyle(openStep === 1, true)}
                >
                  <div style={{ width: STEP_BADGE_WIDTH, flexShrink: 0 }}>
                    <img
                      src="/SVG/zodiyak-step1.svg"
                      alt="Step 1"
                      draggable={false}
                      style={{ width: "100%", height: "auto", display: "block", filter: "drop-shadow(3px 3px 0px #1A1A1A)" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: STEP_LABEL_FONT, fontWeight: 700, color: "#E05A3A", lineHeight: 1 }}>ADD</span>
                    </div>
                    {openStep !== 1 && (
                      <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 16, color: "#888", marginTop: 2 }}>
                        {step2Unlocked ? "Done ✓" : "Start your secret list."}
                      </div>
                    )}
                  </div>
                  <ChevronSvg open={openStep === 1} />
                </button>

                {openStep === 1 && (
                  <div style={accordionBodyStyle}>


                    <LocalCrushList
                      onFirstConfirm={() => setShowHandleSection(true)}
                      onConfirmedCountChange={(count) => setHasConfirmedCrush(count > 0)}
                    />

                    {/* Handle sub-section — appears after first crush confirmed */}
                    {showHandleSection && (
                      <div ref={handleSubRef} style={{ marginTop: 28 }}>
                        {/* Divider */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px" }}>
                          <div style={{ flex: 1, height: 1, background: "rgba(26,26,26,0.12)" }} />

                          <div style={{ flex: 1, height: 1, background: "rgba(26,26,26,0.12)" }} />
                        </div>

                        <LocalHandleList
                          onHandleCountChange={(count) => setHasHandleEntries(count > 0)}
                          onConfirmedHandleCountChange={(n) => setHasConfirmedHandle(n > 0)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── STEP 2: POST ── unlocks when step1 gate passes */}
              <div ref={step2CardRef} style={{ ...sectionCardStyle, opacity: step2Unlocked ? 1 : 0.55 }}>
                {!step2Unlocked && <LockedCardOverlay />}
                <button
                  type="button"
                  onClick={() => { if (step2Unlocked) setOpenStep(openStep === 2 ? null : 2); }}
                  aria-expanded={openStep === 2}
                  aria-disabled={!step2Unlocked}
                  style={stepHeaderStyle(openStep === 2, step2Unlocked)}
                >
                  <div style={{ width: STEP_BADGE_WIDTH, flexShrink: 0 }}>
                    <img
                      src="/SVG/zodiyak-step2.svg"
                      alt="Step 2"
                      draggable={false}
                      style={{ width: "100%", height: "auto", display: "block", filter: "drop-shadow(3px 3px 0px #1A1A1A)" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: STEP_LABEL_FONT, fontWeight: 700, color: "#E05A3A", lineHeight: 1 }}>POST</span>
                    </div>
                    {openStep !== 2 && (
                      <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 16, color: "#888", marginTop: 2 }}>
                        {!step2Unlocked ? "Unlocks after Step 1" : boothDone ? "Done ✓" : "Drop a hint where they'll see it."}
                      </div>
                    )}
                  </div>
                  <ChevronSvg open={openStep === 2} />
                </button>

                {openStep === 2 && step2Unlocked && (
                  <div style={accordionBodyStyle}>

                    <ZodiacPhotoFrame
                      onShareComplete={() => { writeBoothDone(); setBoothDone(true); setOpenStep(3); }}
                      onDismiss={() => { writeBoothDone(); setBoothDone(true); setOpenStep(3); }}
                    />
                    <button
                      onClick={() => { writeBoothDone(); setBoothDone(true); setOpenStep(3); }}
                      style={{
                        display: "block",
                        width: "100%",
                        background: "none",
                        border: "none",
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: 14,
                        fontWeight: 400,
                        color: "rgba(26,26,26,0.5)",
                        cursor: "pointer",
                        padding: "12px 0",
                      }}
                    >
                      Skip for now
                    </button>
                  </div>
                )}
              </div>

              {/* ── STEP 3: MATCH ── unlocks when step2 gate passes */}
              <div ref={step3CardRef} style={{ ...sectionCardStyle, opacity: step3Unlocked ? 1 : 0.55 }}>
                {!step3Unlocked && <LockedCardOverlay />}
                <button
                  type="button"
                  onClick={() => { if (step3Unlocked) setOpenStep(openStep === 3 ? 2 : 3); }}
                  aria-expanded={openStep === 3}
                  aria-disabled={!step3Unlocked}
                  style={stepHeaderStyle(openStep === 3, step3Unlocked)}
                >
                  <div style={{ width: STEP_BADGE_WIDTH, flexShrink: 0 }}>
                    <img
                      src="/SVG/zodiyak-step3.svg"
                      alt="Step 3"
                      draggable={false}
                      style={{ width: "100%", height: "auto", display: "block", filter: "drop-shadow(3px 3px 0px #1A1A1A)" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: STEP_LABEL_FONT, fontWeight: 700, color: "#E05A3A", lineHeight: 1 }}>MATCH</span>
                    </div>
                    {openStep !== 3 && (
                      <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 16, color: "#888", marginTop: 2 }}>
                        {!step3Unlocked ? "Unlocks after Step 2" : "See if they add you."}
                      </div>
                    )}
                  </div>
                  <ChevronSvg open={openStep === 3} />
                </button>

                {openStep === 3 && step3Unlocked && (
                  <div style={accordionBodyStyle}>

                    <PlanSelector
                      ref={signUpFormRef}
                      onSignUpSuccess={(signupEmail) => {
                        void ["Sign up initiated for:", signupEmail];
                      }}
                      onVerificationSuccess={() => {
                        setStage("welcome");
                      }}
                    />
                  </div>
                )}
              </div>

            </div>

            {/* Already a member — below accordion */}
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 20,
                color: "#1A1A1A",
                textAlign: "center",
                margin: "4px 0 20px",
              }}
            >
              Already have an account?{" "}
              <span
                onClick={() => setStage("signin")}
                style={{ color: "#E05A3A", fontWeight: 700, cursor: "pointer" }}
              >
                Sign in
              </span>
            </p>

            {/* MARKETING CARDS */}
            <div className="my-2">
              <MarketingCards />
            </div>

            {/* SECTION 7 — MEME CTA BUTTON 
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "16px 0",
                marginBottom: 40,
              }}
            >
               <button
                onClick={handleMemeButtonClick}
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: "#E05A3A",
                  color: "#FFFFFF",
                  border: "3px solid #1A1A1A",
                  borderRadius: 100,
                  boxShadow: "4px 4px 0px #1A1A1A",
                  padding: "14px 32px",
                  fontFamily: "'Fredoka', sans-serif",
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 700 }}>
                  Got a Crushhh Hint Caption?
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}>
                  Our Favorites Win Full Memberships!
                </span>
              </button>
            </div>*/}

            {/* SECTION 8 — FOUNDER'S NOTE */}
            <div
              style={{
                borderTop: "2px solid rgba(26,26,26,0.12)",
                padding: "44px 24px 60px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  maxWidth: 560,
                  margin: "0 auto",
                  textAlign: "center",
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    fontSize: 24,
                    fontWeight: 600,
                    color: "#1A1A1A",
                    marginTop: 0,
                    marginBottom: 20,
                  }}
                >
                  {FOUNDERS_NOTE_TITLE}
                </h2>
                <p
                  style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 17,
                    lineHeight: 1.75,
                    color: "#1A1A1A",
                    maxWidth: "62ch",
                    margin: "0 auto",
                    textAlign: "left",
                  }}
                >
                  {FOUNDERS_NOTE_BODY}
                </p>
                <p
                  style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 14,
                    color: "rgba(26,26,26,0.5)",
                    marginTop: 20,
                    marginBottom: 20,
                    textAlign: "center",
                  }}
                >
                  {`— ${FOUNDERS_NOTE_ATTRIBUTION}`}
                </p>
              </div>
            </div>
          </>
        )}

        {/* SIGNIN */}
        {stage === "signin" && (
          <div style={{ paddingTop: 16 }}>
            <div
              style={{
                background: "#FFFFFF",
                border: "3px solid #1A1A1A",
                borderRadius: 20,
                boxShadow: "6px 6px 0px #1A1A1A",
                padding: "28px 24px",
                maxWidth: 380,
                margin: "24px auto 0",
                boxSizing: "border-box",
              }}
            >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSignIn();
              }}
            >
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorMessage("");
                }}
                placeholder="Enter your email"
                required
                style={{
                  width: "100%",
                  border: "2.5px solid #E05A3A",
                  borderRadius: 100,
                  padding: "14px 20px",
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 16,
                  background: "#FDF6EE",
                  color: "#1A1A1A",
                  outline: "none",
                  marginBottom: 12,
                  boxSizing: "border-box",
                  boxShadow: "none",
                }}
              />

              <div style={{ position: "relative", marginBottom: 16 }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrorMessage("");
                  }}
                  placeholder="Enter your password"
                  required
                  style={{
                    width: "100%",
                    border: "2.5px solid #E05A3A",
                    borderRadius: 100,
                    padding: "14px 48px 14px 20px",
                    fontFamily: "'Fredoka', sans-serif",
                    fontSize: 16,
                    background: "#FDF6EE",
                    color: "#1A1A1A",
                    outline: "none",
                    boxSizing: "border-box",
                    boxShadow: "none",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-0"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>

              <button
                type="submit"
                disabled={isSigningIn}
                style={{
                  background: "#E05A3A",
                  color: "#FFFFFF",
                  border: "3px solid #1A1A1A",
                  borderRadius: 100,
                  boxShadow: "4px 4px 0px #1A1A1A",
                  padding: "14px 36px",
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                  width: "100%",
                  marginTop: 4,
                  cursor: isSigningIn ? "not-allowed" : "pointer",
                  opacity: isSigningIn ? 0.7 : 1,
                  boxSizing: "border-box",
                  transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
                }}
              >
                {isSigningIn ? "Signing in..." : "Sign In"}
              </button>

              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: "#888",
                  textAlign: "center",
                  marginTop: 16,
                  marginBottom: 0,
                }}
              >
                <span
                  onClick={() => setStage("forgot-password")}
                  style={{ cursor: "pointer" }}
                >
                  Forgot Password or Resend Verification Code
                </span>
              </p>

              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 15,
                  color: "#1A1A1A",
                  textAlign: "center",
                  marginTop: 12,
                  marginBottom: 0,
                }}
              >
                Don&apos;t have an account yet?{" "}
                <span
                  onClick={() => setStage("signup")}
                  style={{
                    color: "#E05A3A",
                    fontWeight: 700,
                    cursor: "pointer",
                  
                  }}
                >
                  Home
                </span>
              </p>
            </form>
            </div>
          </div>
        )}

        {/* FORGOT PASSWORD */}
        {stage === "forgot-password" && (
          <ForgotPassword
            onCancel={() => setStage("signin")}
            onSuccess={() => setStage("signin")}
          />
        )}
      </div>

      <StickyFooter />
    </div>
  );
};

export default PresaleLandingP;